import type {InvestorCheckoutState} from 'Store/ibex/checkouts/sectionsStore';
import type {
  VerificationMethod,
  State,
} from 'Types/deals/investors/verification506cRequests';
import type {InvestorState} from 'Utility/investorFlow/investorState';
import type {DealFeatureFlags} from 'Types/deals/dealFeatureFlags';

export type Attachment506c = {
  id?: number;
  created_at: string;
  file_name: string;
  file_size: number;
  formatted_file_size?: string;
  original_file_name?: string;
  status?: 'completed';
  url?: string;
};

export type InvestorFlowProps = {
  dealMakerUrl: URL;
  apiUrl: URL;
  dealId: string;
  hosted: boolean;
  displayLogo: boolean;
  invitationUid: string;
};

export type Company = {
  id?: number;
  logo?: URL;
  name?: string;
  favicon_image?: URL;
  dmta?: boolean;
};

export type Banner = {
  regular?: string;
  full?: string;
};

export type WhiteLabeling = {
  banner_image?: Banner;
  primary_logo?: string;
  primary_color?: string;
  primary_color_rgb?: {
    primary_r: number;
    primary_g: number;
    primary_b: number;
  };
  background_image?: string;
  custom_favicon?: string;
};

export type PageContent = {
  body?: string;
};

export type PresetAmount = {
  primary: number;
  secondary: number;
  tertiary: number;
};

export type GTMEventData = {
  investor_id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  zip_code?: string;
  phone?: string;
  subtotal_amount?: number;
};

export type AnalyticsTag = {
  provider: string;
  external_tracking_id: string;
  analytics_domain?: string;
};

export type Deal = {
  id: number | undefined;
  security_type: string;
  price_per_security: number | undefined;
  minimum_investment: number | undefined;
  maximum_investment?: number;
  currency: string;
  currency_symbol: string;
  title: string;
  raise_type: string;
  raise_type_code: string;
  singularize_security_type?: string;
  company?: Company;
  white_labeling?: WhiteLabeling;
  title_content?: string;
  page_content?: PageContent;
  preset_amount: PresetAmount;
  document_package_id?: number;
  feature_flags?: DealFeatureFlags;
  experiments?: Experiments;
  setting?: Setting;
  prevent_payments?: boolean;
  redirect_url?: string;
  thank_you_page_url?: string;
  analytics_tags?: AnalyticsTag[];
  free_shares_deal: boolean;
  time_deal: boolean;
  loyalty_deal: boolean;
  volume_deal: boolean;
  maximum_discount_percentage: number;
};

export type Setting = {
  id: number;
  landing_page_phone_number_required: boolean;
  enable_uk_non_accredited_investors: boolean;
  international_investors_unqualified: boolean;
  landing_header?: string;
  disclaimer_page_content: string;
  disclaimer_title_content: string;
  incentive_plan_header: string;
  incentive_plan_content?: string;
  require_name_fields: boolean;
  separate_name_fields: boolean;
  payment_button_crypto?: string;
  payment_button?: string;
};

export type Recaptcha = {
  sitekey: string;
  sitekey_v3: string;
  render: boolean;
};

export type RecaptchaState = {
  recaptcha_token: string;
  recaptcha_invisible: boolean;
};

export type User = {
  id: number | undefined;
  email: string;
  phone: string;
};

export type Verification506cRequest = {
  id: number;
  state: State;
  reminder_email_note: string;
  verification_method: VerificationMethod;
  external_accreditation_verifier?: ExternalAccreditationVerifier;
};

export type ExternalAccreditationVerifier = {
  id: number;
  verification_token: string;
  name: string;
  email: string;
  credential_number: string;
  credential_type: string;
  country: string;
  region: string;
  expired: boolean;
  request_submitted: boolean;
  accredited: boolean;
  investor_name?: string;
};

export type Investor = {
  access_link: URL | null;
  id: number | undefined;
  state: InvestorState;
  created_at: string;
  investment_amount: number | undefined;
  allocated_amount?: number;
  number_of_securities: number | undefined;
  name: string;
  tags: string[];
  user: User;
  checkout_state: InvestorCheckoutState | string;
  phone_number?: string;
  subscription_id?: number;
  status?: string;
  new_investor?: boolean;
  skip_terms_and_conditions?: boolean;
  skip_oci_accreditation_section?: boolean;
  is_funded: boolean;
  funds_pending: boolean;
  enough_total_funds_plus_pending_to_be_funded?: boolean;
  is_agreement_reset: boolean;
  investor_profile_id?: number | undefined;
  postalCode?: string;
  security_price: number;
  aeropay_user_exists: boolean;
  enable_ira_profile: boolean;
  verification_506c_request?: Verification506cRequest;
  attachments_506c?: Attachment506c[];
  experiments?: InvestorExperiments;
};

export type InvestorResponse = {
  access_link: URL | null;
  id: number | undefined;
  state: InvestorState;
  investment_value: number | undefined;
  allocated_amount?: number;
  number_of_securities: number | undefined;
  name: string;
  tags: string[];
  user: User;
  checkout_state: InvestorCheckoutState | string;
  phone_number?: string;
  subscription_id?: number;
  status?: string;
  new_investor?: boolean;
  skip_terms_and_conditions?: boolean;
  skip_oci_accreditation_section?: boolean;
  is_funded: boolean;
  funds_pending: boolean;
  enough_total_funds_plus_pending_to_be_funded?: boolean;
  is_agreement_reset: boolean;
  investor_profile_id?: number | undefined;
  postalCode?: string;
  security_price: number;
  aeropay_user_exists: boolean;
  enable_ira_profile: boolean;
  verification_506c_request?: Verification506cRequest;
  attachments_506c?: Attachment506c[];
  experiments?: InvestorExperiments;
};

export type PersonalInfo = {
  email: string;
  email_confirmation?: string;
  name: string;
  phone: string;
  investment_amount?: number;
  first_name: string;
  last_name: string;
  referrer_email?: string;
  incentive_code?: string;
};

export type SocialProofingData = {
  engagedInvestors: number;
  engagedInvestorsThreshold: number;
  recentInvestors: number;
  recentInvestorDateRangeDays: number;
  recentInvestorsThreshold: number;
  totalInvestors: number;
  totalInvestorsThreshold: number;
};

export type InvestorOtpAccessLink = {
  access_link: URL;
};

export enum ProductPageExperiment {
  DISPLAY_EMAIL_CONFIRMATION = 'display_email_confirmation',
  NO_EMAIL_CONFIRMATION = 'no_email_confirmation',
}

export enum SocialProofingExperiment {
  DISPLAY_SOCIAL_PROOFING = 'display_social_proofing_badges',
  NO_SOCIAL_PROOFING = 'no_social_proofing_badges',
}

export enum SharesPresetExperiment {
  SHARES = 'shares',
  INVESTMENT_AMOUNT = 'investment_amount',
}

export enum SocialLoginExperiment {
  DISPLAY_SOCIAL_LOGIN = 'display_social_login',
  HIDE_SOCIAL_LOGIN = 'hide_social_login',
}

export enum MobileOptimizedFieldsExperiment {
  DISPLAY_MOBILE_OPTIMIZED_FIELDS = 'display_mobile_optimized_fields',
  HIDE_MOBILE_OPTIMIZED_FIELDS = 'hide_mobile_optimized_fields',
}

export enum SteppedNavigationExperiment {
  STEPPED_NAVIGATION = 'stepped_navigation',
  HIDE_STEPPED_NAVIGATION = 'hide_stepped_navigation',
}

export enum InvestorConversionPoweredFooterExperiment {
  DISPLAY_POWERED_FOOTER = 'display_powered_footer',
  HIDE_POWERED_FOOTER = 'hide_powered_footer',
}

export enum AmountSelectionUpdatesExperiment {
  DISPLAY_AMOUNT_SELECTION_UPDATES = 'display_amount_selection_updates',
  HIDE_AMOUNT_SELECTION_UPDATES = 'hide_amount_selection_updates',
}

export type Experiments = {
  product_page_email_conformation?: ProductPageExperiment;
  social_proofing?: SocialProofingExperiment;
  social_login?: SocialLoginExperiment;
  mobile_optimized_fields?: MobileOptimizedFieldsExperiment;
  investor_conversion_stepped_navigation?: SteppedNavigationExperiment;
  investor_conversion_powered_footer?: InvestorConversionPoweredFooterExperiment;
  amount_selection_updates?: AmountSelectionUpdatesExperiment;
};

export type ExperimentResponse = {
  success: boolean;
  experiments: Experiments;
};

export type InvestorExperiments = {
  product_page_shares_preset?: SharesPresetExperiment;
};
